import { useEffect, useMemo, useState } from "react"

import { ExternalLink, Users } from "lucide-react"

import Icon from "@/components/Icon"
import { DriverPerformanceTable } from "@/components/people/tabs/DriverPerformanceTable"
import { LowBlockUptakeTable } from "@/components/people/tabs/LowBlockUptakeTable"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Skeleton } from "@/components/ui/skeleton"
import { Spinner } from "@/components/ui/spinner"
import { OVERVIEW_SHERIFF_TYPES } from "@/constants/sheriffOptions"
import { usePeopleOverview } from "@/hooks/usePeopleOverview"
import { useQueryPeople } from "@/hooks/useQueryPeople"

interface DrawerFilter {
  category: "associateDrivers" | "hivedPartnerDrivers" | "sortationHandlers"
  value: string
}

export const GeneralOverview = () => {
  const { data, isLoading, error } = usePeopleOverview({
    sheriffTypes: OVERVIEW_SHERIFF_TYPES,
  })

  const [selectedLocation, setSelectedLocation] = useState<string | null>(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("people-overview-selected-location") || null
    }
    return null
  })
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerFilter, setDrawerFilter] = useState<DrawerFilter | null>(null)
  const [drawerSearchQuery, setDrawerSearchQuery] = useState<string>("")
  useEffect(() => {
    if (typeof window !== "undefined") {
      if (selectedLocation) {
        localStorage.setItem("people-overview-selected-location", selectedLocation)
      } else {
        localStorage.removeItem("people-overview-selected-location")
      }
    }
  }, [selectedLocation])

  const { data: locationPeopleData, isLoading: isLoadingLocationPeople } = useQueryPeople(
    selectedLocation
      ? {
          first: 5000,
          offset: 0,
          where: {
            status: { equals: "Active" },
            location: { contains: selectedLocation },
          },
        }
      : { first: 0, offset: 0 },
  )

  const locationBreakdown = useMemo(() => {
    const people = (locationPeopleData?.results || []).filter((p) => p.personId)
    const associateDrivers = people.filter(
      (p) => p.type === "AssociateDriver" && p.areas?.some((a) => a === "Rc1 Deliveries"),
    )
    const hivedPartnerDrivers = people.filter(
      (p) =>
        p.type === "TakeHomeVan" &&
        p.areas?.some((a) => a === "Rc1 Deliveries" || a === "Rc2 Deliveries"),
    )
    const sortationHandlers = people.filter(
      (p) => p.type === "HivedVan" && p.areas?.some((a) => a === "Sortation"),
    )
    return { associateDrivers, hivedPartnerDrivers, sortationHandlers }
  }, [locationPeopleData])

  useEffect(() => {
    if (selectedLocation) {
      setDrawerOpen(false)
      setDrawerFilter(null)
      setDrawerSearchQuery("")
    }
  }, [selectedLocation])

  const handlePersonClick = (personId: string | null) => {
    if (!personId) return
    const url = `/people?personId=${encodeURIComponent(personId)}&tab=people`
    window.open(url, "_blank")
  }

  const drawerPeople = drawerFilter ? locationBreakdown[drawerFilter.category] : []
  let filteredDrawerPeople = drawerPeople

  if (drawerSearchQuery.trim()) {
    const searchLower = drawerSearchQuery.toLowerCase().trim()
    filteredDrawerPeople = filteredDrawerPeople.filter((p) => {
      const fullName = (
        p.fullName ||
        `${p.firstName || ""} ${p.lastName || ""}`.trim() ||
        ""
      ).toLowerCase()
      const email = (p.email || "").toLowerCase()
      const phone = (p.phoneNumber || "").toLowerCase()
      const employer = (p.employer || "").toLowerCase()
      const type = (p.type || "").toLowerCase()
      const personId = (p.personId || "").toLowerCase()
      const areas = (p.areas || []).join(" ").toLowerCase()

      return (
        fullName.includes(searchLower) ||
        email.includes(searchLower) ||
        phone.includes(searchLower) ||
        employer.includes(searchLower) ||
        type.includes(searchLower) ||
        personId.includes(searchLower) ||
        areas.includes(searchLower)
      )
    })
  }

  const handleExportCSV = () => {
    if (filteredDrawerPeople.length === 0) return

    const headers = [
      "Person ID",
      "Full Name",
      "First Name",
      "Last Name",
      "Email",
      "Phone",
      "Employer",
      "Type",
      "Areas",
      "Status",
      "Location",
    ]

    const rows = filteredDrawerPeople.map((person) => [
      person.personId || "",
      person.fullName || `${person.firstName || ""} ${person.lastName || ""}`.trim() || "",
      person.firstName || "",
      person.lastName || "",
      person.email || "",
      person.phoneNumber || "",
      person.employer || "",
      person.type || "",
      (person.areas || []).join("; "),
      person.status || "",
      (person.location || []).join("; "),
    ])

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")),
    ].join("\n")

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    const url = URL.createObjectURL(blob)
    link.setAttribute("href", url)
    link.setAttribute("download", `people-export-${new Date().toISOString().split("T")[0]}.csv`)
    link.style.visibility = "hidden"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="flex flex-col gap-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive" className="mb-4">
        <Icon name="warningCircleFilled" className="h-4 w-4" />
        <AlertDescription>
          Failed to load locations: {error instanceof Error ? error.message : String(error)}
        </AlertDescription>
      </Alert>
    )
  }

  const locations = data?.locations ?? {}
  const locationEntries = Object.entries(locations).sort((a, b) => b[1] - a[1])

  const isDrawerLoading = isLoadingLocationPeople

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Locations</h2>

      <div className="flex gap-4">
        {locationEntries.length === 0 ? (
          <div className="text-sm text-palette-jungleGreen500">No locations found</div>
        ) : (
          locationEntries.map(([loc]) => (
            <Card
              key={loc}
              className={`flex-1 cursor-pointer transition-all ${
                selectedLocation === loc ? "bg-primary" : "hover:bg-palette-jungleGreen000"
              }`}
              onClick={() => setSelectedLocation(selectedLocation === loc ? null : loc)}
            >
              <CardContent className="pt-6">
                <div className="text-center">
                  <div
                    className={`text-sm ${selectedLocation === loc ? "text-palette-jungleGreen900" : "text-palette-jungleGreen600"}`}
                  >
                    {loc}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {selectedLocation && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card
            className={`cursor-pointer transition-colors hover:bg-blue-50 border-blue-200 ${
              isLoadingLocationPeople ? "opacity-60" : ""
            }`}
            onClick={() => {
              if (locationBreakdown.associateDrivers.length > 0) {
                setDrawerFilter({ category: "associateDrivers", value: "Associate Drivers" })
                setDrawerOpen(true)
              }
            }}
          >
            <CardContent className="pt-6">
              <div className="flex flex-col">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  <p className="text-sm font-medium text-blue-700">Associate Drivers</p>
                </div>
                {isLoadingLocationPeople ? (
                  <Skeleton className="h-9 w-16" />
                ) : (
                  <p className="text-3xl font-bold text-blue-900">
                    {locationBreakdown.associateDrivers.length}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card
            className={`cursor-pointer transition-colors hover:bg-emerald-50 border-emerald-200 ${
              isLoadingLocationPeople ? "opacity-60" : ""
            }`}
            onClick={() => {
              if (locationBreakdown.hivedPartnerDrivers.length > 0) {
                setDrawerFilter({ category: "hivedPartnerDrivers", value: "HIVED Partner Drivers" })
                setDrawerOpen(true)
              }
            }}
          >
            <CardContent className="pt-6">
              <div className="flex flex-col">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-5 w-5 text-emerald-600" />
                  <p className="text-sm font-medium text-emerald-700">HIVED Partner Drivers</p>
                </div>
                {isLoadingLocationPeople ? (
                  <Skeleton className="h-9 w-16" />
                ) : (
                  <p className="text-3xl font-bold text-emerald-900">
                    {locationBreakdown.hivedPartnerDrivers.length}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card
            className={`cursor-pointer transition-colors hover:bg-amber-50 border-amber-200 ${
              isLoadingLocationPeople ? "opacity-60" : ""
            }`}
            onClick={() => {
              if (locationBreakdown.sortationHandlers.length > 0) {
                setDrawerFilter({ category: "sortationHandlers", value: "Sortation Handlers" })
                setDrawerOpen(true)
              }
            }}
          >
            <CardContent className="pt-6">
              <div className="flex flex-col">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-5 w-5 text-amber-600" />
                  <p className="text-sm font-medium text-amber-700">Sortation Handlers</p>
                </div>
                {isLoadingLocationPeople ? (
                  <Skeleton className="h-9 w-16" />
                ) : (
                  <p className="text-3xl font-bold text-amber-900">
                    {locationBreakdown.sortationHandlers.length}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <LowBlockUptakeTable selectedLocation={selectedLocation} />

      <DriverPerformanceTable selectedLocation={selectedLocation} />

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl flex flex-col p-0">
          <SheetHeader className="flex-shrink-0 px-6 pt-6 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <SheetTitle>{drawerFilter?.value ?? "People"}</SheetTitle>
                <SheetDescription className="mt-1">
                  {selectedLocation && `Location: ${selectedLocation}`}
                  {filteredDrawerPeople.length > 0 && (
                    <span className="ml-2">
                      • {filteredDrawerPeople.length}{" "}
                      {filteredDrawerPeople.length === 1 ? "person" : "people"}
                    </span>
                  )}
                </SheetDescription>
              </div>
              {filteredDrawerPeople.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportCSV}
                  className="flex items-center gap-2"
                >
                  <Icon name="downloadSimple" className="h-4 w-4" />
                  Export CSV
                </Button>
              )}
            </div>
          </SheetHeader>
          <div className="px-6 pb-4 flex-shrink-0">
            <Input
              type="text"
              placeholder="Search by name, email, phone, employer, type, ID, or areas..."
              value={drawerSearchQuery}
              onChange={(e) => setDrawerSearchQuery(e.target.value)}
              className="w-full"
            />
          </div>
          <ScrollArea className="flex-1 px-6 overflow-y-scroll">
            {isDrawerLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Spinner size="lg" />
                <p className="text-sm text-palette-jungleGreen600">Loading people...</p>
                <div className="mt-6 w-full space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-24 w-full" />
                  ))}
                </div>
              </div>
            ) : filteredDrawerPeople.length > 0 ? (
              <div className="space-y-2 py-4">
                {filteredDrawerPeople.map((person) => {
                  const personName =
                    person.person ||
                    person.personId ||
                    person.fullName ||
                    `${person.firstName || ""} ${person.lastName || ""}`.trim() ||
                    "Unknown"

                  return (
                    <Card
                      key={person.id}
                      className="p-3 cursor-pointer border border-palette-jungleGreen200"
                      onClick={() => handlePersonClick(person.personId)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Icon name="user" className="h-5 w-5 text-primary" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex-1">
                              <h3 className="text-base font-semibold text-palette-jungleGreen900">
                                {personName}
                              </h3>
                            </div>
                            <ExternalLink className="h-4 w-4 text-palette-jungleGreen400 flex-shrink-0 mt-0.5" />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 text-sm">
                            {person.email && (
                              <div className="flex items-center gap-1.5 text-palette-jungleGreen600">
                                <Icon
                                  name="envelope"
                                  className="h-3.5 w-3.5 text-palette-jungleGreen400 flex-shrink-0"
                                />
                                <span className="truncate text-xs">{person.email}</span>
                              </div>
                            )}
                            {person.phoneNumber && (
                              <div className="flex items-center gap-1.5 text-palette-jungleGreen600">
                                <Icon
                                  name="phone"
                                  className="h-3.5 w-3.5 text-palette-jungleGreen400 flex-shrink-0"
                                />
                                <span className="truncate text-xs">{person.phoneNumber}</span>
                              </div>
                            )}
                            {person.employer && (
                              <div className="flex items-center gap-1.5 text-palette-jungleGreen600">
                                <Icon
                                  name="company"
                                  className="h-3.5 w-3.5 text-palette-jungleGreen400 flex-shrink-0"
                                />
                                <span className="truncate text-xs">{person.employer}</span>
                              </div>
                            )}
                            {person.type && (
                              <div className="flex items-center gap-1.5 text-palette-jungleGreen600">
                                <Icon
                                  name="truck"
                                  className="h-3.5 w-3.5 text-palette-jungleGreen400 flex-shrink-0"
                                />
                                <span className="truncate text-xs">{person.type}</span>
                              </div>
                            )}
                            {person.areas && person.areas.length > 0 && (
                              <div className="flex items-start gap-1.5 text-palette-jungleGreen600 md:col-span-2">
                                <Icon
                                  name="mapPin"
                                  className="h-3.5 w-3.5 text-palette-jungleGreen400 flex-shrink-0 mt-0.5"
                                />
                                <span className="truncate text-xs">{person.areas.join(", ")}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-palette-jungleGreen500">
                <Icon name="user" className="h-12 w-12 mx-auto mb-3 text-palette-jungleGreen300" />
                <p>No people found</p>
              </div>
            )}
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </div>
  )
}
